/* eslint-disable comma-dangle */
import React, { Suspense, lazy } from 'react';

// Redux
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

// React Dnd
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { Switch } from 'react-router-dom';

// Connected React Router
import { ConnectedRouter } from 'connected-react-router';

// Material UI
import CssBaseline from '@material-ui/core/CssBaseline';
import LinearProgress from '@material-ui/core/LinearProgress';
import { ThemeProvider } from '@material-ui/core/styles';

// Global styles
import GlobalCss from 'styles/Global';

// Store
import store, { history, persistor } from './store';

// Constants
import THEME from './constants/theme';
import PATH from './constants/basePath';

// Components
import {
  DialogRedux,
  DialogSecondRedux,
  Snackbar,
  SecondSnackbar,
  Layout,
} from './components';
import { TransportProvider } from 'pages/public/TicketSales/strategies/transport';

// Routes
const PublicRoute = lazy(() => import('components/Routes/PublicRoute'));
const PrivateRoute = lazy(() => import('components/Routes/PrivateRoute'));

// Public Pages
const Start = lazy(() => import('pages/public/Start'));
const Login = lazy(() => import('pages/public/Login'));
const ForgotPassword = lazy(() => import('pages/public/ForgotPassword'));
const ResetPassword = lazy(() => import('pages/public/ResetPassword'));
const CompanyStartModule = lazy(() => import('pages/public/CompaniesStart'));
const CompanyLogin = lazy(() =>
  import('pages/public/CompaniesStart/pages/Login'),
);
const CompanyForgot = lazy(() =>
  import('pages/public/CompaniesStart/pages/Forgot'),
);

// Transport Operator Module
const Applications = lazy(() => import('pages/bdos/Applications/Applications'));

const Configurations = lazy(() =>
  import('pages/bdos/Attendance/pages/Configurations/Configurations'),
);

const SelectWorkspace = lazy(() =>
  import('pages/bdos/SelectWorkspace/SelectWorkspace'),
);
const AddWorkspace = lazy(() =>
  import('pages/bdos/SelectWorkspace/pages/Add/Add'),
);
const Consortium = lazy(() =>
  import('pages/bdos/SelectWorkspace/pages/Consortium/Consortium'),
);
const SelectModule = lazy(() => import('components/SelectModule/SelectModule'));

const AccountSettings = lazy(() => import('pages/public/AccountSettings'));

const Lines = lazy(() => import('pages/bdos/Lines/Lines'));
const LineAdd = lazy(() => import('pages/bdos/Lines/pages/Add'));
const LineEdit = lazy(() => import('pages/bdos/Lines/pages/Edit'));

const Lots = lazy(() => import('pages/bdos/Lots/Lots'));
const LotAdd = lazy(() => import('pages/bdos/Lots/pages/Add/Add'));
const LotEdit = lazy(() => import('pages/bdos/Lots/pages/Edit/Edit'));

const Sections = lazy(() => import('pages/bdos/Sections/Sections'));
const SectionAdd = lazy(() => import('pages/bdos/Sections/pages/Add/Add'));
const SectionEdit = lazy(() => import('pages/bdos/Sections/pages/Edit/Edit'));

const Itineraries = lazy(() => import('pages/bdos/Itineraries/Itineraries'));
const ItineraryAdd = lazy(() => import('pages/bdos/Itineraries/pages/Add'));
const ItineraryEdit = lazy(() => import('pages/bdos/Itineraries/pages/Edit'));
const ItineraryEditNew = lazy(() =>
  import('pages/bdos/Itineraries/pages/EditNew'),
);
const ItinerariesLine = lazy(() =>
  import('pages/bdos/Itineraries/components/ItineraryLine/ItineraryLine'),
);

const SchedulesMatrix = lazy(() => import('pages/bdos/SchedulesMatrix/'));
const Schedules = lazy(() => import('pages/bdos/Schedules/'));

const Stops = lazy(() => import('pages/bdos/Stops/Stops'));
const StopAdd = lazy(() => import('pages/bdos/Stops/pages/Add/Add'));
const StopEdit = lazy(() => import('pages/bdos/Stops/pages/Edit/Edit'));
const StopCharts = lazy(() => import('pages/bdos/Stops/pages/Charts/Charts'));

const Passengers = lazy(() => import('pages/bdos/Passengers'));
const PassengerAdd = lazy(() => import('pages/bdos/Passengers/pages/Add'));
const PassengerEdit = lazy(() => import('pages/bdos/Passengers/pages/Edit'));

const OnboardSales = lazy(() => import('pages/bdos/OnboardSales'));

const TransportVouchers = lazy(() => import('pages/bdos/TransportVouchers'));
const CollectionInstituition = lazy(() =>
  import('pages/bdos/CollectionInstituition'),
);
const CollectionPassenger = lazy(() =>
  import('pages/bdos/CollectionPassenger'),
);
const Benefits = lazy(() => import('pages/bdos/Benefits'));

const Profiles = lazy(() => import('pages/bdos/Profiles'));

const Bus = lazy(() => import('pages/bdos/Fleet'));
const BusAdd = lazy(() => import('pages/bdos/Fleet/pages/Add'));
const BusEdit = lazy(() => import('pages/bdos/Fleet/pages/Edit'));

const Vessels = lazy(() => import('pages/bdos/Vessels'));
const VesselsAdd = lazy(() => import('pages/bdos/Vessels/pages/Add'));
const VesselsEdit = lazy(() => import('pages/bdos/Vessels/pages/Edit'));

const Plantas = lazy(() => import('pages/bdos/Plantas'));
const PlantasAdd = lazy(() => import('pages/bdos/Plantas/pages/Add'));
const PlantasEdit = lazy(() => import('pages/bdos/Plantas/pages/Edit'));

const Dbds = lazy(() => import('pages/bdos/Dbds'));
const DbdAdd = lazy(() => import('pages/bdos/Dbds/pages/Add'));
const DbdEdit = lazy(() => import('pages/bdos/Dbds/pages/Edit'));

const BugReports = lazy(() => import('pages/bdos/BugReports'));
const BugReportAdd = lazy(() => import('pages/bdos/BugReports/pages/Add'));
const BugReportEdit = lazy(() => import('pages/bdos/BugReports/pages/Edit'));

const SalesNetwork = lazy(() =>
  import('pages/bdos/SalesNetwork/pages/NetworkManagement'),
);
const SalesNetworkEdit = lazy(() =>
  import('pages/bdos/SalesNetwork/pages/NetworkManagement/pages/Edit'),
);
const SalesNetworkRealTimeCashier = lazy(() =>
  import('pages/bdos/SalesNetwork/pages/RealTimeCashier'),
);

const SalesNetworkAdd = lazy(() =>
  import('pages/bdos/SalesNetwork/pages/NetworkManagement/pages/Add'),
);

const SalesNetworkAddModSup = lazy(() =>
  import(
    'pages/bdos/SalesNetwork/components/Navigations/SellingPointsNavigation/pages/Add'
  ),
);
const SalesNetworkEditModSup = lazy(() =>
  import(
    'pages/bdos/SalesNetwork/components/Navigations/SellingPointsNavigation/pages/Edit'
  ),
);
const SellingTerminals = lazy(() => import('pages/bdos/SellingTerminals'));
const InvoicesModule = lazy(() => import('pages/bdos/InvoicesModule'));

const Drivers = lazy(() => import('pages/bdos/Team/pages/Drivers'));
const DriverAdd = lazy(() => import('pages/bdos/Team/pages/Drivers/pages/Add'));
const DriverEdit = lazy(() =>
  import('pages/bdos/Team/pages/Drivers/pages/Edit'),
);

const TicketCollectors = lazy(() =>
  import('pages/bdos/Team/pages/TicketCollectors'),
);
const TicketCollectorAdd = lazy(() =>
  import('pages/bdos/Team/pages/TicketCollectors/pages/Add'),
);
const TicketCollectorEdit = lazy(() =>
  import('pages/bdos/Team/pages/TicketCollectors/pages/Edit'),
);

const Incharge = lazy(() => import('pages/bdos/Team/pages/Incharge'));
const InchargeAdd = lazy(() =>
  import('pages/bdos/Team/pages/Incharge/pages/Add'),
);
const InchargeEdit = lazy(() =>
  import('pages/bdos/Team/pages/Incharge/pages/Edit'),
);
const TeamMachineManagement = lazy(() =>
  import('pages/bdos/Team/pages/MachineManagement'),
);
const SalesNetworkMachineManagement = lazy(() =>
  import('pages/bdos/SalesNetwork/pages/MachineManagement'),
);

const FacialRecognition = lazy(() => import('pages/bdos/FacialRecognition'));
const RegistrationValidation = lazy(() =>
  import('pages/bdos/RegistrationValidation'),
);
const DeviceRelease = lazy(() => import('pages/bdos/DeviceRelease'));
const BoardingInspection = lazy(() => import('pages/bdos/BoardingInspection'));

const Invoices = lazy(() => import('pages/bdos/Invoices'));

const TableEntryReport = lazy(() =>
  import('pages/bdos/Reports/pages/TableEntryReport/TableEntryReport'),
);

const KmPerDayPerCarReport = lazy(() =>
  import('pages/bdos/Reports/pages/KmPerDayPerCarReport/KmPerDayPerCarReport'),
);

const SuperiorUsageReport = lazy(() =>
  import('pages/bdos/Reports/pages/SuperiorUsageReport/SuperiorUsageReport'),
);

const ChartValueReport = lazy(() =>
  import('pages/bdos/Reports/pages/ChartValueReport/ChartValueReport'),
);

const ShipmentByLineReport = lazy(() =>
  import('pages/bdos/Reports/pages/ShipmentByLineReport/ShipmentByLineReport'),
);

const PassengersAndVehiclesReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/PassengersAndVehiclesReport/PassengersAndVehiclesReport'
  ),
);

const PassengersByTerminalReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/PassengersByTerminalReport/PassengersByTerminalReport'
  ),
);

const PassengersAndVehiclesByTripReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/PassengersAndVehiclesByTripReport/PassengersAndVehiclesByTripReport'
  ),
);

const ShipmentsTable = lazy(() =>
  import(
    'pages/bdos/Reports/pages/ShipmentByLineReport/components/ShipmentsTable/ShipmentsTable'
  ),
);

const ChartLineReport = lazy(() =>
  import('pages/bdos/Reports/pages/LineDemandReport/LineDemandReport'),
);

const ChartLineQuantityReport = lazy(() =>
  import('pages/bdos/Reports/pages/LineQuantityReport/LineQuantityReport'),
);

const ChartSegmentReport = lazy(() =>
  import('pages/bdos/Reports/pages/SegmentDemandReport/SegmentDemandReport'),
);

const ChartSegmentQuantityReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/SegmentQuantityReport/SegmentQuantityReport'
  ),
);

const DailyDemandReport = lazy(() =>
  import('pages/bdos/Reports/pages/DailyDemandReport/DailyDemandReport'),
);

const DailyQuantityReport = lazy(() =>
  import('pages/bdos/Reports/pages/DailyQuantityReport/DailyQuantityReport'),
);

const ShipmentDistribution = lazy(() =>
  import('pages/bdos/Reports/pages/ShipmentDistribution/ShipmentDistribution'),
);

const HalfTripDiary = lazy(() =>
  import('pages/bdos/Reports/pages/HalfTripDiary/HalfTripDiary'),
);

const CreditsByCollege = lazy(() =>
  import('pages/bdos/Reports/pages/CreditsByCollege/CreditsByCollege'),
);

const CollectionReport = lazy(() =>
  import('pages/bdos/Reports/pages/CollectionReport/CollectionReport'),
);

const ReceiptsReport = lazy(() =>
  import('pages/bdos/Reports/pages/ReceiptsReport/ReceiptsReport'),
);
const PaymentRequest = lazy(() =>
  import('pages/bdos/Reports/pages/PaymentRequest/PaymentRequest'),
);
const CollectionConsolidation = lazy(() =>
  import(
    'pages/bdos/Reports/pages/CollectionConsolidation/CollectionConsolidation'
  ),
);
const TicketAverageReport = lazy(() =>
  import('pages/bdos/Reports/pages/TicketAverageReport/TicketAverageReport'),
);
const EmvReport = lazy(() =>
  import('pages/bdos/Reports/pages/EmvReport/EmvReport'),
);

const EmvReportOcurrences = lazy(() =>
  import('pages/bdos/Reports/pages/EmvOcurrencesReport/EmvOcurrencesReport'),
);

const SchoolOrdersReport = lazy(() =>
  import('pages/bdos/Reports/pages/SchoolOrdersReport/SchoolOrdersReport'),
);
const BpeReport = lazy(() => import('pages/bdos/Reports/pages/BpeReport'));
const BpeTaxVerificationReport = lazy(() =>
  import('pages/bdos/Reports/pages/BpeTaxVerificationReport'),
);
const AsynchronousReports = lazy(() =>
  import('pages/bdos/Reports/pages/AsynchronousReports/AsynchronousReports'),
);

const StudentCardsReport = lazy(() =>
  import('pages/bdos/Reports/pages/StudentCardsReport/StudentCardsReport'),
);

const CarDemandReport = lazy(() =>
  import('pages/bdos/Reports/pages/CarDemandReport/CarDemandReport'),
);

const CarQuantityReport = lazy(() =>
  import('pages/bdos/Reports/pages/CarQuantityReport/CarQuantityReport'),
);

const CompanyAcquisitionsReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/CompanyAcquisitionsReport/CompanyAcquisitionsReport'
  ),
);

const TransportedPassengersReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/TransportedPassengersReport/TransportedPassengersReport'
  ),
);

const KmDrivedReport = lazy(() =>
  import('pages/bdos/Reports/pages/KmDrivedReport/KmDrivedReport'),
);

const CreditUsageByInstitutionReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/CreditUsageByInstitution/CreditUsageByInstitution'
  ),
);

const PassengersByTimeFrameReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/PassengersByTimeFrameReport/PassengersByTimeFrameReport'
  ),
);

const EmployeeTripResumeReport = lazy(() =>
  import('pages/bdos/Reports/pages/EmployeeTripResume/EmployeeTripResume'),
);

const PassengersTransportedByOriginReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/PassengersTransportedByOriginReport/PassengersTransportedByOriginReport'
  ),
);

const PassengersByTimeFrameByProfileReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/PassengersByTimeFrameByProfileReport/PassengersByTimeFrameByProfileReport'
  ),
);

const ImportattionsReport = lazy(() =>
  import('pages/bdos/Reports/pages/ImportationsReport/ImportationsReport'),
);
const EmtuReport = lazy(() =>
  import('pages/bdos/Reports/pages/EmtuReport/EmtuReport'),
);
const GratuitiesReport = lazy(() =>
  import('pages/bdos/Reports/pages/Gratuities'),
);

const GeneralReport = lazy(() =>
  import('pages/bdos/Reports/pages/GeneralReport/GeneralReport'),
);

const TravelByLine = lazy(() =>
  import('pages/bdos/Reports/pages/TravelByLine/TravelByLine'),
);

const LiveMonitoring = lazy(() => import('pages/bdos/LiveMonitoring'));

const TripMonitoring = lazy(() => import('pages/bdos/TripMonitoring'));
const KmRecord = lazy(() => import('pages/bdos/KmRecord'));
const Fiscalization = lazy(() => import('pages/bdos/Fiscalization'));

const VariableMessageBoard = lazy(() =>
  import('pages/bdos/VariableMessageBoard'),
);
const VariableMessageBoardBusPublic = lazy(() =>
  import('pages/public/VariableMessageBoardBus'),
);
const VariableMessageBoardMetroPublic = lazy(() =>
  import('pages/public/VariableMessageBoardMetro'),
);
const VariableMessageBoardVesselsPublic = lazy(() =>
  import('pages/public/VariableMessageBoardVessels'),
);

const OnboardSalesReport = lazy(() =>
  import('pages/bdos/Reports/pages/OnboardSalesReport/OnboardSalesReport'),
);

const TransportVoucherCollectionReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/TransportVoucherCollectionReport/TransportVoucherCollectionReport'
  ),
);

const OperatorCommissionReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/OperatorCommissionReport/OperatorCommissionReport'
  ),
);

const CompanyManagement = lazy(() => import('pages/bdos/CompanyManagement'));
const SystemManagement = lazy(() => import('pages/bdos/SystemManagement'));

const SupervisorsGroupAdd = lazy(() =>
  import('pages/bdos/CompanyManagement/pages/Groups/pages/Add'),
);
const SupervisorsModules = lazy(() =>
  import('pages/bdos/CompanyManagement/pages/UserModules'),
);
const ModulesManagement = lazy(() =>
  import('pages/bdos/CompanyManagement/pages/Modules/pages/ModulesManagement'),
);

const TransportCards = lazy(() => import('pages/bdos/TransportCards'));

const TransportCardAdd = lazy(() =>
  import('pages/bdos/TransportCards/pages/Add'),
);

const TransportCardEdit = lazy(() =>
  import('pages/bdos/TransportCards/pages/Edit'),
);

const TransportCardPlaceAdd = lazy(() =>
  import('pages/bdos/TransportCards/components/PlaceTable/pages/Add'),
);

const TransportCardPlaceEdit = lazy(() =>
  import('pages/bdos/TransportCards/components/PlaceTable/pages/Edit'),
);

const LineMatrix = lazy(() => import('pages/bdos/LineMatrix'));
const LineMatrixAdd = lazy(() => import('pages/bdos/LineMatrix/pages/Add'));

const MetroStationManagement = lazy(() =>
  import('pages/bdos/MetroStationManagement'),
);

const GarageManagement = lazy(() => import('pages/bdos/GarageManagement'));
const GarageAdd = lazy(() => import('pages/bdos/GarageManagement/pages/Add'));
const GarageEdit = lazy(() => import('pages/bdos/GarageManagement/pages/Edit'));

const TerminalManagement = lazy(() => import('pages/bdos/TerminalManagement'));
const TerminalManagementAdd = lazy(() =>
  import('pages/bdos/TerminalManagement/pages/Add'),
);
const TerminalManagementEdit = lazy(() =>
  import('pages/bdos/TerminalManagement/pages/Edit'),
);

const TerminalBlocks = lazy(() => import('pages/bdos/TerminalBlocks'));

const Tolls = lazy(() => import('pages/bdos/Tolls'));
const TollAdd = lazy(() => import('pages/bdos/Tolls/pages/Add'));
const TollEdit = lazy(() => import('pages/bdos/Tolls/pages/Edit'));

const IntegrationFence = lazy(() => import('pages/bdos/IntegrationFence'));
const IntegrationFenceAdd = lazy(() =>
  import('pages/bdos/IntegrationFence/pages/Add'),
);
const IntegrationFenceEdit = lazy(() =>
  import('pages/bdos/IntegrationFence/pages/Edit'),
);

const Template = lazy(() => import('pages/bdos/Template'));

const Ticketing = lazy(() => import('pages/bdos/Ticketing'));

const Fares = lazy(() => import('pages/bdos/Fares'));

const ChangesByPeriod = lazy(() => import('pages/bdos/ChangesByPeriod'));
const ChangesByPeriodAdd = lazy(() =>
  import('pages/bdos/ChangesByPeriod/pages/Add'),
);
const ChangesByPeriodEdit = lazy(() =>
  import('pages/bdos/ChangesByPeriod/pages/Edit'),
);

const BoardingFares = lazy(() => import('pages/bdos/BoardingFares'));
const BoardingFaresAdd = lazy(() =>
  import('pages/bdos/BoardingFares/pages/Add'),
);
const BoardingFaresEdit = lazy(() =>
  import('pages/bdos/BoardingFares/pages/Edit'),
);

const Turnstiles = lazy(() => import('pages/bdos/Turnstiles'));

const Products = lazy(() => import('pages/bdos/Products'));

const Companies = lazy(() => import('pages/bdos/Companies'));
const CompanyAdd = lazy(() => import('pages/bdos/Companies/pages/Add'));
const CompanyEdit = lazy(() => import('pages/bdos/Companies/pages/Edit'));
const CompanyCreatCollaborator = lazy(() =>
  import('pages/bdos/Companies/pages/AddCollaborator'),
);

const Schools = lazy(() => import('./pages/bdos/Schools'));
const SchoolAdd = lazy(() => import('./pages/bdos/Schools/pages/Add'));
const SchoolEdit = lazy(() => import('./pages/bdos/Schools/pages/Edit'));

// Company Module

const CompanyEditModule = lazy(() =>
  import('pages/companies/Company/pages/Edit'),
);

const CompanyCollaborators = lazy(() =>
  import('pages/companies/Collaborators'),
);
const CollaboratorAdd = lazy(() =>
  import('pages/companies/Collaborators/pages/Add'),
);

const CollaboratorsGroups = lazy(() => import('pages/companies/Groups'));
const CollaboratorsGroupAdd = lazy(() =>
  import('pages/companies/Groups/pages/Add'),
);

const Orders = lazy(() => import('pages/companies/Orders'));
const OrdersHistory = lazy(() =>
  import('pages/companies/Orders/components/OrdersHistory'),
);
const OrdersAdd = lazy(() => import('pages/companies/Orders/pages/Add'));

const CompanyInvoices = lazy(() => import('pages/companies/Invoices'));

// Teaching Institution Module
const TeachingInstitutionModule = lazy(() =>
  import('./pages/teachingInstitutions/Institution'),
);
const TeachingInstitutionModuleEdit = lazy(() =>
  import('./pages/teachingInstitutions/Institution/pages/Edit'),
);

const TeachingInstitutionOrders = lazy(() =>
  import('./pages/teachingInstitutions/Orders'),
);
const TeachingInstitutionOrdersHistory = lazy(() =>
  import('./pages/teachingInstitutions/OrdersHistory'),
);

const Students = lazy(() => import('./pages/teachingInstitutions/Students'));
const StudentAdd = lazy(() =>
  import('./pages/teachingInstitutions/Students/pages/Add'),
);

// Selling Terminal Module
const MySellingTerminal = lazy(() =>
  import('./pages/sellingTerminals/SellingTerminal'),
);

const SellingPoints = lazy(() =>
  import('./pages/sellingTerminals/SellingPoints'),
);
const SellingPointAdd = lazy(() =>
  import('./pages/sellingTerminals/SellingPoints/pages/Points/pages/Add'),
);
const SellingPointEdit = lazy(() =>
  import('./pages/sellingTerminals/SellingPoints/pages/Points/pages/Edit'),
);
const SalesNetworkBySellingTerminals = lazy(() =>
  import('./pages/sellingTerminals/SalesNetworkDetails'),
);

// Attendance Module
const ReplyAdd = lazy(() =>
  import(
    './pages/bdos/Attendance/pages/Configurations/components/Replies/pages/Add/Add'
  ),
);

const TagsAdd = lazy(() =>
  import(
    './pages/bdos/Attendance/pages/Configurations/components/Tags/pages/Add/Add'
  ),
);

const Attendance = lazy(() => import('./pages/bdos/Attendance/ws'));

const AttendantsAdd = lazy(() =>
  import(
    './pages/bdos/Attendance/pages/Configurations/components/Attendants/pages/Add/Add'
  ),
);

const Reports = lazy(() =>
  import('./pages/bdos/Attendance/pages/Reports/Reports'),
);

const ExternalReport = lazy(() => import('pages/bdos/ExternalReport'));

const EmployeeTripSapResumeReport = lazy(() =>
  import(
    'pages/bdos/Reports/pages/EmployeeTripSapResumeReports/EmployeeTripSapResumeReports'
  ),
);

const WebhookAdd = lazy(() =>
  import(
    './pages/bdos/Attendance/pages/Configurations/components/Webhook/pages/Add/Add'
  ),
);

// Notifications and Segmentations
const Notifications = lazy(() =>
  import('./pages/bdos/Relations/Notifications'),
);
const NotificationsAdd = lazy(() =>
  import('pages/bdos/Relations/Notifications/pages/Add'),
);
const NotificationsEdit = lazy(() =>
  import('pages/bdos/Relations/Notifications/pages/Edit'),
);

const Segmentations = lazy(() =>
  import('./pages/bdos/Relations/Segmentations'),
);
const SegmentationsAdd = lazy(() =>
  import('pages/bdos/Relations/Segmentations/pages/Add'),
);
const SegmentationsEdit = lazy(() =>
  import('pages/bdos/Relations/Segmentations/pages/Edit'),
);

// @ts-ignore
const NotAllowed = lazy(() => import('pages/bdos/NotAllowed'));
const NotFound = lazy(() => import('pages/bdos/NotFound'));

// Connectivity
const DataPlan = lazy(() =>
  import('pages/bdos/Connectivity/pages/DataPlan/DataPlan'),
);
const ConectionCover = lazy(() =>
  import('pages/bdos/Connectivity/pages/ConectionCover/ConectionCover'),
);

// Changelog History
const ChangelogHistory = lazy(() =>
  import('pages/bdos/ChangelogHistory/ChangelogHistory'),
);

const UsageStatistics = lazy(() =>
  import('pages/bdos/Reports/pages/UsageStatistics/UsageStatisticsReport'),
);

const ProfileByAgeGroupe = lazy(() =>
  import('pages/bdos/Reports/pages/ProfileByAgeGroupe/ProfileByAgeGroupe'),
);

const TicketSales = lazy(() => import('pages/public/TicketSales'));
const TicketDetails = lazy(() =>
  import('pages/public/TicketSales/pages/TicketDetails'),
);
const TicketSalesLogin = lazy(() =>
  import('pages/public/TicketSales/pages/Login'),
);
const PaymentDetails = lazy(() =>
  import('pages/public/TicketSales/pages/PaymentDetails'),
);
const TicketSalesOrders = lazy(() =>
  import('pages/public/TicketSales/pages/Orders'),
);
const TicketSalesMyBpe = lazy(() =>
  import('pages/public/TicketSales/pages/MyBpe'),
);

const TicketSalesProfile = lazy(() =>
  import('pages/public/TicketSales/pages/Profile'),
);

const GetNetWebView = lazy(() => import('pages/public/GetNetWebView'));

/*
 * !!! Clear all Redux Persist !!!
 * persistor.purge();
 */

const App: React.FC = () => (
  <>
    <GlobalCss />
    <CssBaseline />
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider theme={THEME}>
          <DndProvider backend={HTML5Backend}>
            <ConnectedRouter history={history}>
              <Layout />
              <Suspense fallback={<LinearProgress />}>
                <Switch>
                  {/* Página Inicial */}
                  <PublicRoute component={Start} exact path={PATH.HOME} />
                  {/* Login  */}
                  <PublicRoute component={Login} exact path={PATH.LOGIN} />
                  {/* Recuperar Senha */}
                  <PublicRoute
                    component={ForgotPassword}
                    exact
                    path={PATH.FORGOT_PASSWORD}
                  />
                  {/* Resetar Senha */}
                  <PublicRoute
                    component={ResetPassword}
                    exact
                    path={PATH.RESET_PASSWORD}
                  />
                  {/* Empresas: Página Inicial */}
                  <PublicRoute
                    component={CompanyStartModule}
                    exact
                    path={PATH.COMPANY_MODULE.BASE_URL}
                  />
                  {/* Empresas: Login */}
                  <PublicRoute
                    component={CompanyLogin}
                    exact
                    path={PATH.COMPANY_MODULE.LOGIN}
                  />
                  {/* Empresas: Forgot */}
                  <PublicRoute
                    component={CompanyForgot}
                    exact
                    path={PATH.COMPANY_MODULE.FORGOT_PASSWORD}
                  />
                  {/* Painel de mensagems variáveis: Bus */}
                  <PublicRoute
                    component={VariableMessageBoardBusPublic}
                    exact
                    path={PATH.VARIABLE_MESSAGE_BOARD_PUBLIC.BUS}
                  />
                  {/* Painel de mensagems variáveis: Metro */}
                  <PublicRoute
                    component={VariableMessageBoardMetroPublic}
                    exact
                    path={PATH.VARIABLE_MESSAGE_BOARD_PUBLIC.METRO}
                  />
                  {/* Painel de mensagems variáveis: Balça */}
                  <PublicRoute
                    component={VariableMessageBoardVesselsPublic}
                    exact
                    path={PATH.VARIABLE_MESSAGE_BOARD_PUBLIC.VESSELS}
                  />
                  {/* Venda de passagens */}
                  <PublicRoute
                    component={TicketSales}
                    exact
                    path={PATH.TICKET_RESERVATION_WEBSITE.BASE_URL}
                  />
                  <PublicRoute
                    exact
                    path={PATH.TICKET_RESERVATION_WEBSITE.TICKET_DETAILS}
                    render={() => (
                      <TransportProvider>
                        <TicketDetails />
                      </TransportProvider>
                    )}
                  />
                  <PublicRoute
                    component={TicketSalesLogin}
                    exact
                    path={PATH.TICKET_RESERVATION_WEBSITE.LOGIN}
                  />
                  <PrivateRoute
                    component={PaymentDetails}
                    exact
                    path={PATH.TICKET_RESERVATION_WEBSITE.TO_PAY}
                    redirectPath={PATH.TICKET_RESERVATION_WEBSITE.LOGIN}
                  />
                  <PrivateRoute
                    component={TicketSalesOrders}
                    exact
                    path={PATH.TICKET_RESERVATION_WEBSITE.MY_ORDERS}
                  />
                  <PrivateRoute
                    component={TicketSalesMyBpe}
                    exact
                    path={PATH.TICKET_RESERVATION_WEBSITE.MY_BPE}
                  />
                  <PrivateRoute
                    component={TicketSalesProfile}
                    exact
                    path={PATH.TICKET_RESERVATION_WEBSITE.PROFILE}
                  />
                  {/* GetNetWebView */}
                  <PublicRoute
                    component={GetNetWebView}
                    exact
                    path={PATH.GET_NET_WEB_VIEW.ALL_PARAMS}
                  />
                  <PublicRoute
                    component={GetNetWebView}
                    exact
                    path={PATH.GET_NET_WEB_VIEW.PARAMS}
                  />
                  {/* Account Settings  */}
                  <PrivateRoute
                    component={AccountSettings}
                    exact
                    path={PATH.ACCOUNT_SETTINGS_MODULE.BASE_URL}
                  />
                  {/* Module */}
                  <PrivateRoute
                    component={SelectModule}
                    exact
                    path={PATH.SELECT_MODULE}
                  />
                  {/* Workspace */}
                  <PrivateRoute
                    component={SelectWorkspace}
                    exact
                    path={PATH.SELECT_WORKSPACE.BASE_URL}
                  />
                  <PrivateRoute
                    component={AddWorkspace}
                    exact
                    path={PATH.SELECT_WORKSPACE.CREATE}
                  />
                  <PrivateRoute
                    component={Consortium}
                    exact
                    path={PATH.SELECT_WORKSPACE.CREATE_CONSORTIUM}
                  />
                  {/* Passageiros */}
                  <PrivateRoute
                    component={Passengers}
                    exact
                    path={PATH.PASSENGER.BASE_URL}
                  />
                  <PrivateRoute
                    component={PassengerAdd}
                    exact
                    path={PATH.PASSENGER.CREATE}
                  />
                  <PrivateRoute
                    component={PassengerEdit}
                    exact
                    path={PATH.PASSENGER.EDIT}
                  />
                  {/* Empresas */}
                  <PrivateRoute
                    component={Companies}
                    exact
                    path={PATH.COMPANIES.BASE_URL}
                  />
                  <PrivateRoute
                    component={CompanyAdd}
                    exact
                    path={PATH.COMPANIES.CREATE}
                  />
                  <PrivateRoute
                    component={CompanyEdit}
                    exact
                    path={PATH.COMPANIES.EDIT}
                  />
                  <PrivateRoute
                    component={CompanyCreatCollaborator}
                    exact
                    path={PATH.COMPANIES.CREATE_COLLABORATOR}
                  />
                  {/* Bilhetagem */}
                  <PrivateRoute
                    component={Ticketing}
                    exact
                    path={PATH.TICKETING.BASE_URL}
                  />
                  {/* Paradas */}
                  <PrivateRoute
                    component={Stops}
                    exact
                    path={PATH.STOPS.BASE_URL}
                  />
                  <PrivateRoute
                    component={StopAdd}
                    exact
                    path={PATH.STOPS.CREATE}
                  />
                  <PrivateRoute
                    component={StopEdit}
                    exact
                    path={PATH.STOPS.EDIT}
                  />
                  <PrivateRoute
                    component={StopCharts}
                    exact
                    path={PATH.STOPS.CHART}
                  />
                  {/* Seções */}
                  <PrivateRoute
                    component={Sections}
                    exact
                    path={PATH.SECTIONS.BASE_URL}
                  />
                  <PrivateRoute
                    component={SectionAdd}
                    exact
                    path={PATH.SECTIONS.CREATE}
                  />
                  <PrivateRoute
                    component={SectionEdit}
                    exact
                    path={PATH.SECTIONS.EDIT}
                  />
                  {/* Gestão de garagens */}
                  <PrivateRoute
                    component={GarageManagement}
                    exact
                    path={PATH.GARAGE.BASE_URL}
                  />
                  <PrivateRoute
                    component={GarageAdd}
                    exact
                    path={PATH.GARAGE.CREATE}
                  />
                  <PrivateRoute
                    component={GarageEdit}
                    exact
                    path={PATH.GARAGE.EDIT}
                  />
                  {/* Gestão de terminais */}
                  <PrivateRoute
                    component={TerminalManagement}
                    exact
                    path={PATH.TERMINAL_MANAGEMENT.BASE_URL}
                  />
                  <PrivateRoute
                    component={TerminalManagementAdd}
                    exact
                    path={PATH.TERMINAL_MANAGEMENT.CREATE}
                  />
                  <PrivateRoute
                    component={TerminalManagementEdit}
                    exact
                    path={PATH.TERMINAL_MANAGEMENT.EDIT}
                  />
                  {/* Bloqueios de terminais */}
                  <PrivateRoute
                    component={TerminalBlocks}
                    exact
                    path={PATH.TERMINAL_BLOCKS.BASE_URL}
                  />
                  {/* Pedágios */}
                  <PrivateRoute
                    component={Tolls}
                    exact
                    path={PATH.TOLLS.BASE_URL}
                  />
                  <PrivateRoute
                    component={TollAdd}
                    exact
                    path={PATH.TOLLS.CREATE}
                  />
                  <PrivateRoute
                    component={TollEdit}
                    exact
                    path={PATH.TOLLS.EDIT}
                  />
                  {/* Integration Fence */}
                  <PrivateRoute
                    component={IntegrationFence}
                    exact
                    path={PATH.INTEGRATION_FENCE.BASE_URL}
                  />
                  <PrivateRoute
                    component={IntegrationFenceAdd}
                    exact
                    path={PATH.INTEGRATION_FENCE.CREATE}
                  />
                  <PrivateRoute
                    component={IntegrationFenceEdit}
                    exact
                    path={PATH.INTEGRATION_FENCE.EDIT}
                  />
                  {/* Linhas */}
                  <PrivateRoute
                    component={Lines}
                    exact
                    path={PATH.LINES.BASE_URL}
                  />
                  <PrivateRoute
                    component={LineAdd}
                    exact
                    path={PATH.LINES.CREATE}
                  />
                  <PrivateRoute
                    component={LineEdit}
                    exact
                    path={PATH.LINES.EDIT}
                  />
                  {/* Itinerários */}
                  <PrivateRoute
                    component={Itineraries}
                    exact
                    path={PATH.ITINERARIES.BASE_URL}
                  />
                  <PrivateRoute
                    component={ItineraryAdd}
                    exact
                    path={PATH.ITINERARIES.CREATE}
                  />
                  <PrivateRoute
                    component={ItineraryEditNew}
                    exact
                    path={PATH.ITINERARIES.EDIT}
                  />
                  <PrivateRoute
                    component={ItinerariesLine}
                    exact
                    path={PATH.ITINERARIES.ITINERARIES_LINE}
                  />
                  {/* Matriz de Escalas */}
                  <PrivateRoute
                    component={SchedulesMatrix}
                    exact
                    path={PATH.SCHEDULES_MATRIX.BASE_URL}
                  />
                  {/* Escalas */}
                  <PrivateRoute
                    component={Schedules}
                    exact
                    path={PATH.SCHEDULES.BASE_URL}
                  />
                  {/* Tarifas */}
                  <PrivateRoute
                    component={Fares}
                    exact
                    path={PATH.FARES.BASE_URL}
                  />
                  {/* Alteraçõçes por período */}
                  <PrivateRoute
                    component={ChangesByPeriod}
                    exact
                    path={PATH.CHANGES_BY_PERIOD.BASE_URL}
                  />
                  <PrivateRoute
                    component={ChangesByPeriodAdd}
                    exact
                    path={PATH.CHANGES_BY_PERIOD.CREATE}
                  />
                  <PrivateRoute
                    component={ChangesByPeriodEdit}
                    exact
                    path={PATH.CHANGES_BY_PERIOD.EDIT}
                  />
                  {/* Taxas de Embarque */}
                  <PrivateRoute
                    component={BoardingFares}
                    exact
                    path={PATH.BOARDING_FARES.BASE_URL}
                  />
                  <PrivateRoute
                    component={BoardingFaresAdd}
                    exact
                    path={PATH.BOARDING_FARES.CREATE}
                  />
                  <PrivateRoute
                    component={BoardingFaresEdit}
                    exact
                    path={PATH.BOARDING_FARES.EDIT}
                  />
                  {/* Catracas */}
                  <PrivateRoute
                    component={Turnstiles}
                    exact
                    path={PATH.TURNSTILES.BASE_URL}
                  />
                  {/* Produtos */}
                  <PrivateRoute
                    component={Products}
                    exact
                    path={PATH.PRODUCTS.BASE_URL}
                  />
                  {/* Perfis */}
                  <PrivateRoute
                    component={Profiles}
                    exact
                    path={PATH.PROFILES.BASE_URL}
                  />
                  {/* Lotes */}
                  <PrivateRoute
                    component={Lots}
                    exact
                    path={PATH.LOTS.BASE_URL}
                  />
                  <PrivateRoute
                    component={LotAdd}
                    exact
                    path={PATH.LOTS.CREATE}
                  />
                  <PrivateRoute
                    component={LotEdit}
                    exact
                    path={PATH.LOTS.EDIT}
                  />
                  {/* Frota */}
                  <PrivateRoute
                    component={Bus}
                    exact
                    path={PATH.FLEET.BASE_URL}
                  />
                  <PrivateRoute
                    component={BusAdd}
                    exact
                    path={PATH.FLEET.CREATE}
                  />
                  <PrivateRoute
                    component={BusEdit}
                    exact
                    path={PATH.FLEET.EDIT}
                  />
                  <PrivateRoute
                    component={Vessels}
                    exact
                    path={PATH.VESSELS.BASE_URL}
                  />
                  <PrivateRoute
                    component={VesselsAdd}
                    exact
                    path={PATH.VESSELS.CREATE}
                  />
                  <PrivateRoute
                    component={VesselsEdit}
                    exact
                    path={PATH.VESSELS.EDIT}
                  />
                  <PrivateRoute
                    component={Plantas}
                    exact
                    path={PATH.PLANTAS.BASE_URL}
                  />
                  <PrivateRoute
                    component={PlantasAdd}
                    exact
                    path={PATH.PLANTAS.CREATE}
                  />
                  <PrivateRoute
                    component={PlantasEdit}
                    exact
                    path={PATH.PLANTAS.EDIT}
                  />
                  {/* Dbds */}
                  <PrivateRoute
                    component={Dbds}
                    exact
                    path={PATH.DBDS.BASE_URL}
                  />
                  <PrivateRoute
                    component={DbdAdd}
                    exact
                    path={PATH.DBDS.CREATE}
                  />
                  <PrivateRoute
                    component={DbdEdit}
                    exact
                    path={PATH.DBDS.EDIT}
                  />
                  {/* Vendas a Bordo */}
                  <PrivateRoute
                    component={SalesNetwork}
                    exact
                    path={PATH.SALES_NETWORK.BASE_URL}
                  />
                  <PrivateRoute
                    component={SalesNetworkEdit}
                    exact
                    path={PATH.SALES_NETWORK.EDIT}
                  />
                  <PrivateRoute
                    component={SalesNetworkRealTimeCashier}
                    exact
                    path={PATH.SALES_NETWORK.REAL_TIME_CASHIER}
                  />
                  <PrivateRoute
                    component={SalesNetworkAdd}
                    exact
                    path={PATH.SALES_NETWORK.CREATE}
                  />
                  <PrivateRoute
                    component={SalesNetworkAddModSup}
                    exact
                    path={PATH.SALES_NETWORK.ADDMODSUP}
                  />
                  <PrivateRoute
                    component={SalesNetworkEditModSup}
                    exact
                    path={PATH.SALES_NETWORK.EDITMODSUP}
                  />
                  <PrivateRoute
                    component={SalesNetworkMachineManagement}
                    exact
                    path={PATH.SALES_NETWORK_MACHINE_MANAGEMENT.BASE_URL}
                  />
                  {/* Arrecadação */}
                  <PrivateRoute
                    component={SellingTerminals}
                    exact
                    path={PATH.SELLING_TERMINALS.BASE_URL}
                  />
                  <PrivateRoute
                    component={OnboardSales}
                    exact
                    path={PATH.ONBOARD_SALES.BASE_URL}
                  />
                  <PrivateRoute
                    component={TransportVouchers}
                    exact
                    path={PATH.TRANSPORT_VOUCHER.BASE_URL}
                  />
                  <PrivateRoute
                    component={CollectionInstituition}
                    exact
                    path={PATH.COLLECTION_INSTITUITION.BASE_URL}
                  />
                  <PrivateRoute
                    component={CollectionPassenger}
                    exact
                    path={PATH.COLLECTION_PASSENGER.BASE_URL}
                  />
                  <PrivateRoute
                    component={InvoicesModule}
                    exact
                    path={PATH.INVOICES_MODULE.BASE_URL}
                  />
                  {/* Equipe */}
                  <PrivateRoute
                    component={Drivers}
                    exact
                    path={PATH.DRIVERS.BASE_URL}
                  />
                  <PrivateRoute
                    component={DriverAdd}
                    exact
                    path={PATH.DRIVERS.CREATE}
                  />
                  <PrivateRoute
                    component={DriverEdit}
                    exact
                    path={PATH.DRIVERS.EDIT}
                  />
                  <PrivateRoute
                    component={TicketCollectors}
                    exact
                    path={PATH.TICKET_COLLECTORS.BASE_URL}
                  />
                  <PrivateRoute
                    component={TicketCollectorAdd}
                    exact
                    path={PATH.TICKET_COLLECTORS.CREATE}
                  />
                  <PrivateRoute
                    component={TicketCollectorEdit}
                    exact
                    path={PATH.TICKET_COLLECTORS.EDIT}
                  />
                  <PrivateRoute
                    component={Incharge}
                    exact
                    path={PATH.IN_CHARGE.BASE_URL}
                  />
                  <PrivateRoute
                    component={InchargeAdd}
                    exact
                    path={PATH.IN_CHARGE.CREATE}
                  />
                  <PrivateRoute
                    component={InchargeEdit}
                    exact
                    path={PATH.IN_CHARGE.EDIT}
                  />
                  <PrivateRoute
                    component={TeamMachineManagement}
                    exact
                    path={PATH.MACHINE_MANAGEMENT.BASE_URL}
                  />
                  {/* BugReports */}
                  <PrivateRoute
                    component={BugReports}
                    exact
                    path={PATH.BUG_REPORTS.BASE_URL}
                  />
                  <PrivateRoute
                    component={BugReportAdd}
                    exact
                    path={PATH.BUG_REPORTS.CREATE}
                  />
                  <PrivateRoute
                    component={BugReportEdit}
                    exact
                    path={PATH.BUG_REPORTS.EDIT}
                  />
                  {/* Reconhecimento Facial */}
                  <PrivateRoute
                    component={FacialRecognition}
                    exact
                    path={PATH.FACIAL_RECOGNITION.BASE_URL}
                  />
                  {/* Validação Cadastral */}
                  <PrivateRoute
                    component={RegistrationValidation}
                    exact
                    path={PATH.REGISTRATION_VALIDATION.BASE_URL}
                  />
                  {/* Liberação de dispositivo */}
                  <PrivateRoute
                    component={DeviceRelease}
                    exact
                    path={PATH.DEVICE_RELEASE.BASE_URL}
                  />
                  <PrivateRoute
                    component={DeviceRelease}
                    exact
                    path={PATH.DEVICE_RELEASE.FROM_PASSENGER}
                  />
                  {/* Inspeção de Embarques */}
                  <PrivateRoute
                    component={BoardingInspection}
                    exact
                    path={PATH.BOARDING_INSPECTION.BASE_URL}
                  />
                  {/* Relatórios de Entrada */}
                  <PrivateRoute
                    component={TableEntryReport}
                    exact
                    path={PATH.TABLE_ENTRY_REPORT.BASE_URL}
                  />
                  {/* Relatórios de Km Rodados por Dia */}
                  <PrivateRoute
                    component={KmPerDayPerCarReport}
                    exact
                    path={PATH.KM_PER_DAY_PER_CAR_REPORT.BASE_URL}
                  />
                  {/* Relatório de Uso Superior */}
                  <PrivateRoute
                    component={SuperiorUsageReport}
                    exact
                    path={PATH.REPORT_SUPERIOR_ACCESS.BASE_URL}
                  />
                  {/* Relatórios de gráficos de valor */}
                  <PrivateRoute
                    exact
                    path={PATH.CHART_VALUE_REPORT.BASE_URL}
                    component={ChartValueReport}
                  />
                  {/* Embarque por Linha */}
                  <PrivateRoute
                    exact
                    path={PATH.SHIPMENT_BY_LINE_REPORT.BASE_URL}
                    component={ShipmentByLineReport}
                  />
                  {/* Passageiros e Veículos */}
                  <PrivateRoute
                    exact
                    path={PATH.PASSENGERS_AND_VEHICLES.BASE_URL}
                    component={PassengersAndVehiclesReport}
                  />
                  {/* Passageiros e Veículos por viagem */}
                  <PrivateRoute
                    exact
                    path={PATH.PASSENGERS_AND_VEHICLES_BY_TRIP.BASE_URL}
                    component={PassengersAndVehiclesByTripReport}
                  />
                  {/* Passageiros por Terminal */}
                  <PrivateRoute
                    exact
                    path={PATH.PASSENGERS_BY_TERMINAL.BASE_URL}
                    component={PassengersByTerminalReport}
                  />
                  {/* Relatórios de Demanda por linha */}
                  <PrivateRoute
                    exact
                    path={PATH.LINE_DEMAND_REPORT.BASE_URL}
                    component={ChartLineReport}
                  />
                  <PrivateRoute
                    exact
                    path={PATH.LINE_QUANTITY_REPORT.BASE_URL}
                    component={ChartLineQuantityReport}
                  />
                  <PrivateRoute
                    exact
                    path={PATH.SEGMENT_DEMAND_REPORT.BASE_URL}
                    component={ChartSegmentReport}
                  />
                  <PrivateRoute
                    exact
                    path={PATH.SEGMENT_QUANTITY_REPORT.BASE_URL}
                    component={ChartSegmentQuantityReport}
                  />
                  {/* Relatórios de Demanda por dia */}
                  <PrivateRoute
                    exact
                    path={PATH.DAILY_DEMAND_REPORT.BASE_URL}
                    component={DailyDemandReport}
                  />
                  {/* Relatórios de Quantidade por dia */}
                  <PrivateRoute
                    exact
                    path={PATH.DAILY_QUANTITY_REPORT.BASE_URL}
                    component={DailyQuantityReport}
                  />
                  {/* Relatórios de distribuição de embarque */}
                  <PrivateRoute
                    component={ShipmentDistribution}
                    exact
                    path={PATH.SHIPMENT_DISTRIBUTION.BASE_URL}
                  />
                  {/* Diário de meia Viagem */}
                  <PrivateRoute
                    component={HalfTripDiary}
                    exact
                    path={PATH.HALF_TRIP_DIARY.BASE_URL}
                  />
                  {/* Créditos por Colégio */}
                  <PrivateRoute
                    component={CreditsByCollege}
                    exact
                    path={PATH.CREDITS_BY_COLLEGE.BASE_URL}
                  />
                  {/* Relatório de Arrecadação */}
                  <PrivateRoute
                    component={CollectionReport}
                    exact
                    path={PATH.COLLECTION_REPORT.BASE_URL}
                  />
                  {/* Relatório de Recebimentos */}
                  <PrivateRoute
                    component={ReceiptsReport}
                    exact
                    path={PATH.RECEIPTS_REPORT.BASE_URL}
                  />
                  {/* Relatório de Requisição e pagamento */}
                  <PrivateRoute
                    component={PaymentRequest}
                    exact
                    path={PATH.PAYMENT_REQUEST_REPORT.BASE_URL}
                  />
                  {/* Relatórios de Consolidação da Arrecadação */}
                  <PrivateRoute
                    component={CollectionConsolidation}
                    exact
                    path={PATH.COLLECTION_CONSOLIDATION_REPORT.BASE_URL}
                  />
                  {/* Relatório de Captação */}
                  <PrivateRoute
                    component={TicketAverageReport}
                    exact
                    path={PATH.TICKET_AVERAGE_REPORT.BASE_URL}
                  />
                  {/* Relatório de Emv */}
                  <PrivateRoute
                    component={EmvReport}
                    exact
                    path={PATH.EMV_REPORT.BASE_URL}
                  />
                  {/* Relatórios de Ocorrencias Emv */}
                  <PrivateRoute
                    component={EmvReportOcurrences}
                    exact
                    path={PATH.EMV_REPORT_OCURRENCES.BASE_URL}
                  />
                  {/* Relatório Consolidado por Escola */}
                  <PrivateRoute
                    component={SchoolOrdersReport}
                    exact
                    path={PATH.SCHOOL_ORDER_REPORT.BASE_URL}
                  />
                  <PrivateRoute
                    component={BpeReport}
                    exact
                    path={PATH.BPE_REPORT.BASE_URL}
                  />
                  <PrivateRoute
                    component={BpeTaxVerificationReport}
                    exact
                    path={PATH.BPE_TAX_VERIFICATION_REPORT.BASE_URL}
                  />
                  <PrivateRoute
                    component={AsynchronousReports}
                    exact
                    path={PATH.ASYNCHRONOUS_REPORTS.BASE_URL}
                  />

                  {/* Relatório de cartoes por aluno */}
                  <PrivateRoute
                    component={StudentCardsReport}
                    exact
                    path={PATH.REPORT_STUDENTS_DEVICE.BASE_URL}
                  />
                  {/* Relatório de Vendas a Bordo */}
                  <PrivateRoute
                    component={OnboardSalesReport}
                    exact
                    path={PATH.ONBOARD_SALES_REPORT.BASE_URL}
                  />
                  {/* Relatório de Arrecadação - Vale-transporte */}
                  <PrivateRoute
                    component={TransportVoucherCollectionReport}
                    exact
                    path={PATH.TRANSPORT_VOUCHER_COLLECTION_REPORT.BASE_URL}
                  />
                  {/* Relátorio de Comissão de Operadores */}
                  <PrivateRoute
                    component={OperatorCommissionReport}
                    exact
                    path={PATH.OPERATOR_COMMISSION_REPORT.BASE_URL}
                  />
                  {/* Relatório de Demanda por Carro */}
                  <PrivateRoute
                    component={CarDemandReport}
                    exact
                    path={PATH.CAR_DEMAND_REPORT.BASE_URL}
                  />
                  {/* Relatório de Quantidade por Carro */}
                  <PrivateRoute
                    exact
                    path={PATH.CAR_QUANTITY_REPORT.BASE_URL}
                    component={CarQuantityReport}
                  />
                  {/* Relatório de Compras por Empresa */}
                  <PrivateRoute
                    component={CompanyAcquisitionsReport}
                    exact
                    path={PATH.COMPANY_ACQUISITIONS_REPORT.BASE_URL}
                  />
                  {/* Relatório de Passageiros Transportados */}
                  <PrivateRoute
                    component={TransportedPassengersReport}
                    exact
                    path={PATH.TRANSPORTED_PASSENGERS_REPORT.BASE_URL}
                  />
                  {/* Relatório de KM Rodados */}
                  <PrivateRoute
                    component={KmDrivedReport}
                    exact
                    path={PATH.KM_DRIVED_REPORT.BASE_URL}
                  />
                  {/* Relatório de Utilização de Créditos por instituição */}
                  <PrivateRoute
                    component={CreditUsageByInstitutionReport}
                    exact
                    path={PATH.CREDIT_USAGE_BY_INSTITUTION_REPORT.BASE_URL}
                  />
                  {/* Relatório de Passageiros por Faixa Horária */}
                  <PrivateRoute
                    component={PassengersByTimeFrameReport}
                    exact
                    path={PATH.PASSENGERS_BY_TIME_FRAME_REPORT.BASE_URL}
                  />
                  {/* Relatório de Resumo de Viagems */}
                  <PrivateRoute
                    component={EmployeeTripResumeReport}
                    exact
                    path={PATH.EMPLOYEE_TRIP_RESUME_REPORT.BASE_URL}
                  />
                  {/* Relatório de Passageiros Transportados por Origem */}
                  <PrivateRoute
                    component={PassengersTransportedByOriginReport}
                    exact
                    path={PATH.PASSENGERS_TRANSPORTED_BY_ORIGIN_REPORT.BASE_URL}
                  />
                  {/* Relatório de Passageiros por Faixa Horária / Perfil */}
                  <PrivateRoute
                    component={PassengersByTimeFrameByProfileReport}
                    exact
                    path={
                      PATH.PASSENGERS_BY_TIME_FRAME_BY_PROFILE_REPORT.BASE_URL
                    }
                  />
                  {/* Relatório de Importações */}
                  <PrivateRoute
                    component={ImportattionsReport}
                    exact
                    path={PATH.COMPANY_IMPORTATIONS_REPORT.BASE_URL}
                  />
                  {/* Relatório EMTU */}
                  <PrivateRoute
                    component={EmtuReport}
                    exact
                    path={PATH.EMTU_REPORT.BASE_URL}
                  />
                  {/* Relatório Gratuidades */}
                  <PrivateRoute
                    component={GratuitiesReport}
                    exact
                    path={PATH.GRATUITIES_REPORT.BASE_URL}
                  />
                  {/* Relatório de Operação • Passageiros Transportados */}
                  <PrivateRoute
                    component={GeneralReport}
                    exact
                    path={PATH.GENERAL_REPORT.BASE_URL}
                  />
                  {/* Relatório Viagens por Linha */}
                  <PrivateRoute
                    component={TravelByLine}
                    exact
                    path={PATH.TRAVEL_BY_LINE_REPORT.BASE_URL}
                  />
                  {/* Relatório Perfil por Faixa Etária */}
                  <PrivateRoute
                    component={ProfileByAgeGroupe}
                    exact
                    path={PATH.PROFILE_BY_AGE_GROUP_REPORT.BASE_URL}
                  />
                  {/* Employee Trip Sap Resume Report */}
                  <PrivateRoute
                    component={EmployeeTripSapResumeReport}
                    path={PATH.EMPLOYEE_TRIP_SAP_RESUME_REPORT.BASE_URL}
                  />
                  {/* Relatório Externo */}
                  <PrivateRoute
                    component={ExternalReport}
                    path={PATH.EXTERNAL_REPORTS.BASE_URL}
                  />
                  {/* Monitoramento Ao Vivo */}
                  <PrivateRoute
                    component={LiveMonitoring}
                    exact
                    path={PATH.LIVE_MONITORING.BASE_URL}
                  />
                  {/* Monitoramento de Viagens */}
                  <PrivateRoute
                    component={TripMonitoring}
                    exact
                    path={PATH.TRIP_MONITORING.BASE_URL}
                  />
                  {/* Painel de mensagems variáveis */}
                  <PrivateRoute
                    component={VariableMessageBoard}
                    exact
                    path={PATH.VARIABLE_MESSAGE_BOARD.BASE_URL}
                  />
                  {/* Registro de km */}
                  <PrivateRoute
                    component={KmRecord}
                    exact
                    path={PATH.KM_RECORD.BASE_URL}
                  />
                  {/* Fiscalização */}
                  <PrivateRoute
                    component={Fiscalization}
                    exact
                    path={PATH.FISCALIZATION.BASE_URL}
                  />
                  {/* Faturas */}
                  <PrivateRoute
                    component={Invoices}
                    exact
                    path={PATH.INVOICES.BASE_URL}
                  />
                  {/* Aplicativos */}
                  <PrivateRoute
                    component={Applications}
                    exact
                    path={PATH.APPLICATIONS.BASE_URL}
                  />
                  {/* Gerenciar Empresa */}
                  <PrivateRoute
                    component={CompanyManagement}
                    exact
                    path={PATH.COMPANY_MANAGEMENT.BASE_URL}
                  />
                  <PrivateRoute
                    component={SupervisorsGroupAdd}
                    exact
                    path={PATH.COMPANY_MANAGEMENT.CREATE_GROUP}
                  />
                  <PrivateRoute
                    component={SupervisorsModules}
                    exact
                    path={PATH.COMPANY_MANAGEMENT.MANAGEMENT_MODULES}
                  />
                  <PrivateRoute
                    component={ModulesManagement}
                    exact
                    path={PATH.COMPANY_MANAGEMENT.MODULES_MANAGEMENT}
                  />
                  {/* Gerenciar Sistema */}
                  <PrivateRoute
                    component={SystemManagement}
                    exact
                    path={PATH.SYSTEM_MANAGEMENT.BASE_URL}
                  />
                  {/* Regras de Negócios */}
                  <PrivateRoute
                    component={Benefits}
                    exact
                    path={PATH.BENEFITS.BASE_URL}
                  />
                  {/* Cartões de Transporte */}
                  <PrivateRoute
                    component={TransportCards}
                    exact
                    path={PATH.TRANSPORT_CARDS.BASE_URL}
                  />
                  <PrivateRoute
                    component={TransportCardAdd}
                    exact
                    path={PATH.TRANSPORT_CARDS.CREATE}
                  />
                  <PrivateRoute
                    component={TransportCardEdit}
                    exact
                    path={PATH.TRANSPORT_CARDS.EDIT}
                  />
                  {/* Cartões de Transporte - Local */}
                  <PrivateRoute
                    component={TransportCardPlaceAdd}
                    exact
                    path={PATH.TRANSPORT_CARDS.CREATE_LOCAL}
                  />
                  <PrivateRoute
                    component={TransportCardPlaceEdit}
                    exact
                    path={PATH.TRANSPORT_CARDS.EDIT_LOCAL}
                  />
                  {/* Bilhetagem • Integrações */}
                  <PrivateRoute
                    component={LineMatrix}
                    exact
                    path={PATH.LINE_MATRIX.BASE_URL}
                  />
                  <PrivateRoute
                    component={LineMatrixAdd}
                    exact
                    path={PATH.LINE_MATRIX.CREATE}
                  />
                  {/* Bilhetagem • Gestão de Estações */}
                  <PrivateRoute
                    component={MetroStationManagement}
                    exact
                    path={PATH.METRO_STATION_MANAGEMENT.BASE_URL}
                  />
                  {/* Instituições de Ensino */}
                  <PrivateRoute
                    component={Schools}
                    exact
                    path={PATH.SCHOOLS.BASE_URL}
                  />
                  <PrivateRoute
                    component={SchoolAdd}
                    exact
                    path={PATH.SCHOOLS.CREATE}
                  />
                  <PrivateRoute
                    component={SchoolEdit}
                    exact
                    path={PATH.SCHOOLS.EDIT}
                  />
                  {/* Template */}
                  <PrivateRoute
                    component={Template}
                    exact
                    path={PATH.TEMPLATE.BASE_URL}
                  />
                  {/* Atendimento */}
                  <PrivateRoute
                    component={Attendance}
                    exact
                    path={PATH.ATTENDANCE.BASE_URL}
                  />
                  <PrivateRoute
                    component={Configurations}
                    exact
                    path={PATH.ATTENDANCE.CONFIGURATIONS}
                  />
                  <PrivateRoute
                    component={Reports}
                    exact
                    path={PATH.ATTENDANCE.REPORTS}
                  />
                  <PrivateRoute
                    component={TagsAdd}
                    exact
                    path={PATH.ATTENDANCE.CREATE_TAGS}
                  />
                  <PrivateRoute
                    component={ReplyAdd}
                    exact
                    path={PATH.ATTENDANCE.CREATE_REPLY}
                  />
                  <PrivateRoute
                    component={AttendantsAdd}
                    exact
                    path={PATH.ATTENDANCE.CREATE_ATTENDANTS}
                  />
                  <PrivateRoute
                    component={WebhookAdd}
                    exact
                    path={PATH.ATTENDANCE.CREATE_WEBHOOK}
                  />
                  {/* Empresas: Minha Empresa Editar */}
                  <PrivateRoute
                    component={CompanyEditModule}
                    exact
                    path={PATH.COMPANY_MODULE.EDIT}
                  />
                  {/* Empresas: Colaboradores */}
                  <PrivateRoute
                    component={CompanyCollaborators}
                    exact
                    path={PATH.COMPANY_COLLABORATORS.BASE_URL}
                  />
                  <PrivateRoute
                    component={CollaboratorAdd}
                    exact
                    path={PATH.COMPANY_COLLABORATORS.CREATE}
                  />
                  {/* Empresas: Grupos */}
                  <PrivateRoute
                    component={CollaboratorsGroups}
                    exact
                    path={PATH.COMPANY_GROUP.BASE_URL}
                  />
                  <PrivateRoute
                    component={CollaboratorsGroupAdd}
                    exact
                    path={PATH.COMPANY_GROUP.CREATE}
                  />
                  {/* Empresas: Pedidos */}
                  <PrivateRoute
                    component={Orders}
                    exact
                    path={PATH.COMPANY_ORDERS.BASE_URL}
                  />
                  <PrivateRoute
                    component={OrdersAdd}
                    exact
                    path={PATH.COMPANY_ORDERS.CREATE}
                  />
                  <PrivateRoute
                    component={OrdersHistory}
                    exact
                    path={PATH.COMPANY_ORDERS_HISTORY.BASE_URL}
                  />
                  <PrivateRoute
                    component={Orders}
                    exact
                    path={PATH.COMPANY_ORDERS.DUPLICATE_ORDER}
                  />
                  {/* Empresas: Faturas */}
                  <PrivateRoute
                    component={CompanyInvoices}
                    exact
                    path={PATH.COMPANY_INVOICES.BASE_URL}
                  />
                  {/* Instituições de Ensino: Minha Instituição */}
                  <PrivateRoute
                    component={TeachingInstitutionModule}
                    exact
                    path={PATH.TEACHING_INSTITUTION_MODULE.BASE_URL}
                  />
                  <PrivateRoute
                    component={TeachingInstitutionModuleEdit}
                    exact
                    path={PATH.TEACHING_INSTITUTION_MODULE.EDIT}
                  />
                  {/* Instituições de Ensino: Estudantes */}
                  <PrivateRoute
                    component={Students}
                    exact
                    path={PATH.TEACHING_INSTITUTION_STUDENTS.BASE_URL}
                  />
                  <PrivateRoute
                    component={StudentAdd}
                    exact
                    path={PATH.TEACHING_INSTITUTION_STUDENTS.CREATE}
                  />
                  {/* Instituições de Ensino: Pedidos de VT */}
                  <PrivateRoute
                    component={TeachingInstitutionOrders}
                    exact
                    path={PATH.TEACHING_INSTITUTION_ORDERS.BASE_URL}
                  />
                  <PrivateRoute
                    component={TeachingInstitutionOrdersHistory}
                    exact
                    path={PATH.TEACHING_INSTITUTION_ORDERS_HISTORY.BASE_URL}
                  />
                  {/* Redes de Venda: Pontos de Venda */}
                  <PrivateRoute
                    component={MySellingTerminal}
                    exact
                    path={PATH.SELLING_TERMINAL.BASE_URL}
                  />
                  <PrivateRoute
                    component={SellingPoints}
                    exact
                    path={PATH.SELLING_POINTS.BASE_URL}
                  />
                  <PrivateRoute
                    component={SellingPointAdd}
                    exact
                    path={PATH.SELLING_POINTS.CREATE}
                  />
                  <PrivateRoute
                    component={SellingPointEdit}
                    exact
                    path={PATH.SELLING_POINTS.EDIT}
                  />
                  {/* Redes de Venda: Pontos de Venda */}
                  <PrivateRoute
                    component={SalesNetworkBySellingTerminals}
                    exact
                    path={PATH.SELLING_TERMINAL_DETAILS.BASE_URL}
                  />
                  {/* Notificações e Segmentações */}
                  <PrivateRoute
                    component={Notifications}
                    exact
                    path={PATH.NOTIFICATIONS.BASE_URL}
                  />
                  <PrivateRoute
                    component={NotificationsAdd}
                    exact
                    path={PATH.NOTIFICATIONS.CREATE}
                  />
                  <PrivateRoute
                    component={NotificationsEdit}
                    exact
                    path={PATH.NOTIFICATIONS.EDIT}
                  />
                  <PrivateRoute
                    component={Segmentations}
                    exact
                    path={PATH.SEGMENTATIONS.BASE_URL}
                  />
                  <PrivateRoute
                    component={SegmentationsAdd}
                    exact
                    path={PATH.SEGMENTATIONS.CREATE}
                  />
                  <PrivateRoute
                    component={SegmentationsEdit}
                    exact
                    path={PATH.SEGMENTATIONS.EDIT}
                  />
                  <PrivateRoute
                    component={NotAllowed}
                    exact
                    path={PATH.NOT_ALLOWED.BASE_URL}
                  />
                  {/* Conectividade */}
                  <PrivateRoute
                    component={DataPlan}
                    exact
                    path={PATH.DATA_PLAN_CONNECTIVITY.BASE_URL}
                  />
                  <PrivateRoute
                    component={ConectionCover}
                    exact
                    path={PATH.CONECTION_COVER_CONNECTIVITY.BASE_URL}
                  />
                  {/* Changelog History */}
                  <PrivateRoute
                    component={ChangelogHistory}
                    exact
                    path={PATH.CHANGELOG_HISTORY.BASE_URL}
                  />
                  {/* Usage Statistics Report */}
                  <PrivateRoute
                    component={UsageStatistics}
                    exact
                    path={PATH.USAGE_STATISTICS.BASE_URL}
                  />
                  <PrivateRoute component={NotFound} />
                </Switch>
              </Suspense>
              <DialogRedux />
              <DialogSecondRedux />
            </ConnectedRouter>
          </DndProvider>
          <Snackbar />
          <SecondSnackbar />
        </ThemeProvider>
      </PersistGate>
    </Provider>
  </>
);

export default App;
